// Import all property images as modules
import property1 from '@/assets/property-1.jpg';
import property2 from '@/assets/property-2.jpg';
import property3 from '@/assets/property-3.jpg';
import heroApartment from '@/assets/hero-apartment.jpg';
import aqarnetLogo from '@/assets/aqarnet-logo.png';

// Map of image paths to actual imported URLs
const imageMap: Record<string, string> = {
  '/src/assets/property-1.jpg': property1,
  'src/assets/property-1.jpg': property1,
  'property-1.jpg': property1,
  
  '/src/assets/property-2.jpg': property2,
  'src/assets/property-2.jpg': property2,
  'property-2.jpg': property2,
  
  '/src/assets/property-3.jpg': property3,
  'src/assets/property-3.jpg': property3,
  'property-3.jpg': property3,
  
  '/src/assets/hero-apartment.jpg': heroApartment,
  'src/assets/hero-apartment.jpg': heroApartment,
  'hero-apartment.jpg': heroApartment,
};

// Function to resolve image path to proper URL
export function resolveImageUrl(imagePath: string | undefined | null): string {
  // Handle undefined or null inputs
  if (!imagePath) {
    return '';
  }
  
  // If it's already a proper URL (http/https), return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // If it's a blob URL, return as is
  if (imagePath.startsWith('blob:')) {
    return imagePath;
  }

  // If it's a Supabase storage URL (contains supabase.co), return as is
  if (imagePath.includes('supabase.co')) {
    return imagePath;
  }
  
  // Check if we have a mapping for this path
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }
  
  // Try to find a partial match
  const fileName = imagePath.split('/').pop() || '';
  const matchingKey = Object.keys(imageMap).find(key => key.includes(fileName));
  if (matchingKey) {
    return imageMap[matchingKey];
  }
  
  // Fallback: return the original path (might still work for public URLs)
  return imagePath;
}

// Export the default images for easy access
export { property1, property2, property3, heroApartment, aqarnetLogo };

// Image optimization utilities
export interface ImageOptimizationOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'jpeg' | 'png' | 'auto';
}

/**
 * Get optimized image URL for services that support URL-based transformations
 */
export function getOptimizedImageUrl(
  url: string, 
  options: ImageOptimizationOptions = {}
): string {
  const { width, height, quality = 85, format = 'auto' } = options;

  // Skip optimization for local assets
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return url;
  }

  // Supabase Storage transformation
  if (url.includes('supabase.co/storage')) {
    const params = new URLSearchParams();
    if (width) params.set('width', String(width));
    if (height) params.set('height', String(height));
    if (quality !== 85) params.set('quality', String(quality));
    
    const separator = url.includes('?') ? '&' : '?';
    return params.toString() ? `${url}${separator}${params.toString()}` : url;
  }

  // Unsplash URL transformation
  if (url.includes('unsplash.com')) {
    const params = new URLSearchParams();
    if (width) params.set('w', String(width));
    if (height) params.set('h', String(height));
    params.set('q', String(quality));
    params.set('auto', 'format');
    
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${params.toString()}`;
  }

  // Cloudinary URL transformation
  if (url.includes('cloudinary.com')) {
    const transformations = [];
    if (width) transformations.push(`w_${width}`);
    if (height) transformations.push(`h_${height}`);
    transformations.push(`q_${quality}`);
    transformations.push('f_auto');
    
    return url.replace('/upload/', `/upload/${transformations.join(',')}/`);
  }

  return url;
}

/**
 * Generate srcset for responsive images
 */
export function generateSrcSet(
  url: string,
  widths: number[] = [400, 600, 800, 1200, 1600]
): string {
  return widths
    .map(w => `${getOptimizedImageUrl(url, { width: w })} ${w}w`)
    .join(', ');
}

/**
 * Get recommended image dimensions based on device pixel ratio
 */
export function getRetinaAdjustedSize(
  baseWidth: number,
  pixelRatio: number = typeof window !== 'undefined' ? window.devicePixelRatio : 1
): number {
  // Cap at 2x to avoid unnecessarily large images
  const effectiveRatio = Math.min(pixelRatio, 2);
  return Math.round(baseWidth * effectiveRatio);
}